// React
import React from 'react'

// Next
import Head from 'next/head'
import { GetStaticProps } from 'next'

// Components
import TopicMemes from '@/components/website/TopicMemes'
import Layout from '@/components/website/core/Layout'
import WhySupermeme from '@/components/website/WhySupermeme'
import CTAComponent from '@/components/website/core/CTA'
import TextSection from '@/components/website/core/TextSection'
import YouMightAlsoLike from '@/components/website/YouMightAlsoLike'
import LinkedText from '@/components/website/core/LinkedText'

// Utils
import { getLandingStaticProps } from '@/utils/ui/landing/get-static-props'
import { LinkedContent } from '@/utils/ui/types/linked-content'

// External
import { useTranslation } from 'next-i18next'

const CustomerServiceMemes: React.FC = () => {
  const { t } = useTranslation('routes/customer-service-memes')

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: t('meta.title'),
    description: t('meta.description'),
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': 'https://supermeme.ai/memes/customer-service-memes',
    },
    publisher: {
      '@type': 'Organization',
      name: 'SuperMeme.ai',
      logo: {
        '@type': 'ImageObject',
        url: 'https://supermeme.ai/static/logo-big.png',
      },
    },
  }

  const renderListItemWithStrong = (item: {
    title: string
    description: string | LinkedContent
  }) => (
    <>
      <strong>{item.title}:</strong>{' '}
      {typeof item.description === 'string' ? (
        item.description
      ) : (
        <LinkedText content={item.description} />
      )}
    </>
  )

  return (
    <Layout>
      <Head>
        <title>{t('meta.title')}</title>
        <meta name='description' content={t('meta.description')} />
        <meta
          property='og:url'
          content='https://supermeme.ai/memes/customer-service-memes'
        />
        <link
          rel='canonical'
          href='https://supermeme.ai/memes/customer-service-memes'
        />
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
      </Head>

      <TopicMemes
        heading={t('hero.heading')}
        description={t('hero.description')}
        imageFolder='static/landing-pages/memes-customer-service-memes'
      />

      <div className='mx-auto max-w-4xl space-y-8 py-12 sm:py-16'>
        <YouMightAlsoLike />
      </div>
      <div className='mx-auto max-w-4xl space-y-8 py-12 sm:py-16'>
        <WhySupermeme />
        <CTAComponent />
        <TextSection
          h2={t('benefits.title')}
          p={
            <LinkedText
              content={
                t('benefits.description', {
                  returnObjects: true,
                }) as LinkedContent
              }
            />
          }
        />
        <TextSection p={t('benefits.atmosphere')} />
        <TextSection p={t('benefits.sharing')} />
        <TextSection
          h2={t('humor.title')}
          p={
            <LinkedText
              content={
                t('humor.description', { returnObjects: true }) as LinkedContent
              }
            />
          }
        />
        <TextSection p={t('humor.culture')} />
        <TextSection
          h2={t('howTo.title')}
          p={
            <LinkedText
              content={
                t('howTo.description', { returnObjects: true }) as LinkedContent
              }
            />
          }
          li={(
            t('howTo.tips', { returnObjects: true }) as {
              title: string
              description: string | LinkedContent
            }[]
          ).map(renderListItemWithStrong)}
        />
        <TextSection
          h2={t('guidelines.title')}
          p={t('guidelines.description')}
        />
        <TextSection
          h3={t('guidelines.dos.title')}
          li={(
            t('guidelines.dos.list', { returnObjects: true }) as {
              title: string
              description: string | LinkedContent
            }[]
          ).map(renderListItemWithStrong)}
        />
        <TextSection
          h3={t('guidelines.donts.title')}
          li={(
            t('guidelines.donts.list', { returnObjects: true }) as {
              title: string
              description: string | LinkedContent
            }[]
          ).map(renderListItemWithStrong)}
        />
        <TextSection
          h2={t('faqs.title')}
          li={(
            t('faqs.questions', { returnObjects: true }) as {
              question: string
              answer: string | LinkedContent
            }[]
          ).map(({ question, answer }, index) => (
            <>
              <strong>{question}</strong>{' '}
              {typeof answer === 'string' ? (
                answer
              ) : (
                <LinkedText content={answer} />
              )}
            </>
          ))}
        />
      </div>
    </Layout>
  )
}

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  return getLandingStaticProps({
    locale,
    namespaces: [
      'common',
      'routes/customer-service-memes',
      'components/why-supermeme',
      'components/cta',
    ],
  })
}

export default CustomerServiceMemes
